import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAuthHeaders,
  workspaceBackendUrl,
} from "../components/SupabaseClient";

// One authoritative backend catalog for display and experiment selection.
export const useDatasets = () => {
  const requestId = useRef(0);
  const [datasets, setDatasets] = useState([]);
  const [datasetCount, setDatasetCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDatasets = useCallback(async () => {
    const currentRequest = ++requestId.current;
    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      if (!headers) throw new Error("Sign in to view datasets.");
      const resp = await fetch(`${workspaceBackendUrl}/cluster/datasets/all`, {
        headers,
      });
      if (!resp.ok) {
        throw new Error(
          `Datasets API error: ${resp.status} ${resp.statusText}`,
        );
      }
      const datasetList = await resp.json();
      const normalizedDatasets = (datasetList.datasets || [])
        .slice()
        .sort((a, b) =>
          String(a.title || "").localeCompare(String(b.title || "")),
        );

      if (currentRequest !== requestId.current) return;
      setDatasets(normalizedDatasets);
      setDatasetCount(datasetList.datasets_cnt || 0);
    } catch (err) {
      if (currentRequest !== requestId.current) return;
      console.error("Failed to get datasets:", err);
      setError(err.message || "Unable to load datasets.");
      setDatasets([]);
      setDatasetCount(0);
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDatasets();
    return () => {
      requestId.current += 1;
    };
  }, [fetchDatasets]);

  return {
    datasets,
    datasetCount,
    loading,
    error,
    refreshDatasets: fetchDatasets,
  };
};
